// MembersLiveChart.jsx - IMPROVED DESIGN
import React, { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";
import { fetchLiveMembersEntries } from "../api/api";

const MembersLiveChart = () => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const statusColors = {
    working: "var(--success)",
    break: "var(--warning)",
    idle: "var(--danger)",
    offline: "var(--text-tertiary)"
  };

  const buildSeries = (data) => {
    const now = Date.now();
    return data.map((member) => ({
      name: member.user_name,
      data: member.intervals.map((interval) => ({
        x: member.user_name,
        y: [
          new Date(interval.start_time).getTime(),
          interval.end_time ? new Date(interval.end_time).getTime() : now,
        ],
        fillColor: statusColors[interval.status] || "#6B7280",
      })),
    }));
  };

  const getTodayRange = () => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 0, 0); // 8 AM
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 20, 0, 0); // 8 PM
    return [startOfDay.getTime(), endOfDay.getTime()];
  };

  const loadChart = async () => {
    try {
      const membersData = await fetchLiveMembersEntries();
      const series = buildSeries(membersData);

      if (chartInstance.current) {
        chartInstance.current.updateSeries(series, true);
      }
    } catch (err) {
      console.error("Failed to load live members chart:", err);
    }
  };

  useEffect(() => {
    const initChart = async () => {
      try {
        const membersData = await fetchLiveMembersEntries();
        const [xMin, xMax] = getTodayRange();

        const options = {
          chart: {
            type: "rangeBar",
            height: 280,
            toolbar: { show: false },
            background: "transparent",
            fontFamily: 'inherit',
            animations: {
              enabled: true,
              speed: 800
            }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "60%",
              rangeBarGroupRows: true,
              borderRadius: 4,
              columnWidth: "60%"
            }
          },
          series: buildSeries(membersData),
          xaxis: {
            type: "datetime",
            min: xMin,
            max: xMax,
            labels: {
              datetimeFormatter: {
                hour: 'HH:mm',
                minute: 'HH:mm'
              },
              style: {
                colors: "var(--text-tertiary)",
                fontSize: "10px",
                fontFamily: 'inherit'
              }
            },
            axisBorder: {
              show: true,
              color: 'var(--border-light)'
            },
            axisTicks: {
              show: true,
              color: 'var(--border-light)'
            }
          },
          yaxis: {
            labels: {
              style: {
                colors: "var(--text-primary)",
                fontSize: "11px",
                fontWeight: 500,
                fontFamily: 'inherit'
              }
            }
          },
          grid: {
            borderColor: "var(--border-light)",
            strokeDashArray: 3,
            padding: {
              top: 10,
              right: 10,
              bottom: 10,
              left: 10
            }
          },
          fill: {
            type: "solid",
            opacity: 0.8
          },
          tooltip: {
            custom: ({ seriesIndex, dataPointIndex, w }) => {
              const data = w.config.series[seriesIndex]?.data[dataPointIndex];
              if (!data) return "";
              
              const start = new Date(data.y[0]).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              });
              const end = new Date(data.y[1]).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              });
              
              const status = Object.entries(statusColors)
                .find(([, color]) => color === data.fillColor)?.[0] 
                || "Unknown";
              
              return `
                <div class="apex-tooltip">
                  <div class="tooltip-header">
                    <strong>${data.x}</strong>
                    <span class="tooltip-status ${status}">${status}</span>
                  </div>
                  <div class="tooltip-time">
                    <i class='bx bx-time'></i>
                    ${start} - ${end}
                  </div>
                </div>
              `;
            }
          },
          legend: {
            show: false
          },
          dataLabels: {
            enabled: false
          }
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();
      } catch (err) {
        console.error("Failed to initialize chart:", err);
      }
    };

    initChart();
    const interval = setInterval(loadChart, 5000);

    return () => {
      clearInterval(interval);
      chartInstance.current?.destroy();
    };
  }, []);

  return (
    <div className="live-members-chart">
      <div ref={chartRef} />
      
      <style jsx>{`
        .live-members-chart {
          width: 100%;
          height: 280px;
          padding: 8px 0;
        }
        
        .apex-tooltip {
          background: var(--bg-primary);
          border: 1px solid var(--border-light);
          border-radius: var(--radius);
          padding: 10px;
          box-shadow: var(--shadow-md);
          font-family: inherit;
        }
        
        .tooltip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          gap: 10px;
        }
        
        .tooltip-header strong {
          color: var(--text-primary);
          font-size: 0.875rem;
          font-weight: 600;
        }
        
        .tooltip-status {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 10px;
          background: var(--bg-tertiary);
          color: var(--text-secondary);
        }
        
        .tooltip-status.working {
          background: var(--success-light);
          color: var(--success);
        }
        
        .tooltip-status.break {
          background: var(--warning-light);
          color: var(--warning);
        }
        
        .tooltip-status.idle {
          background: var(--danger-light);
          color: var(--danger);
        }
        
        .tooltip-time {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--text-secondary);
          font-size: 0.75rem;
        }
        
        .tooltip-time i {
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
};

export default MembersLiveChart;